import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, featuredVideos, latestVideos } from '@/mocks/videos';

const allVideos = [...featuredVideos, ...latestVideos.map((v) => ({ ...v, views: '新增', url: `/article/${v.id}` }))];

const VIDEOS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'hongtao');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setPage(1);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug, page]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug],
  );

  const categoryVideos = useMemo(
    () => allVideos.filter((v) => v.categorySlug === activeSlug),
    [activeSlug],
  );

  const totalPages = Math.max(1, Math.ceil(categoryVideos.length / VIDEOS_PER_PAGE));
  const pagedVideos = categoryVideos.slice(
    (page - 1) * VIDEOS_PER_PAGE,
    page * VIDEOS_PER_PAGE,
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    setPage(1);
    navigate(`/category/${newSlug}`);
  };

  return (
    <>
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.taozivideo.com/' },
            { '@type': 'ListItem', position: 2, name: activeCategory.name },
          ],
        }}
      />
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: `${activeCategory.name} - 桃子视频`,
          description: activeCategory.description,
          url: `https://www.taozivideo.com/category/${activeCategory.slug}`,
        }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{activeCategory.name}</span>
            </nav>

            {/* Category Header */}
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                {activeCategory.name}
              </h1>
              <p className="text-sm text-foreground-600">
                {activeCategory.description}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-1 overflow-x-auto pb-2 mb-8 flex-wrap">
              <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Video Grid */}
            {pagedVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
                {pagedVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={video.url}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                      <img
                        src={video.image}
                        alt={video.title}
                        title={`${video.title} - ${video.category}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-accent-500 text-background-50">
                          {'rating' in video ? video.rating : '新'}
                        </span>
                      </div>
                      {'episode' in video && video.episode && (
                        <div className="absolute bottom-2 right-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-black/70 text-white">
                            {video.episode}
                          </span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {video.type} · {video.year}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-film-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无内容，敬请期待</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      page === p
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}